import { useTranslations } from '@vocab/react';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext } from 'react-hook-form';

import { VirusScanStatus } from 'src/__generated__/graphql';
import { PageSection, useTrackingClient } from 'src/context/tracking';
import { uploadSelectionCriteria } from 'src/context/upload-attachments';
import {
  AttachmentOptionFile,
  AttachmentOptionText,
  AttachmentSelect,
  FileAttachment,
} from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import {
  MAX_CHARS,
  useValidators,
} from 'src/forms/common/AttachmentSelect/hooks/useValidators';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useVirusScan } from 'src/hooks/useVirusScan';
import { isFile } from 'src/utils';

import translations from './.vocab';

export interface SelectionCriteriaFieldsValues {
  'selectionCriteria-method': FileAttachment;
  'selectionCriteria-uri': string;
  'selectionCriteria-file': File;
  'selectionCriteria-text': string;
  'selectionCriteria-virusScan': unknown;
}

interface Props {
  defaultMethod?: FileAttachment;
  defaultValue?: string | File;
  hideTitle?: boolean;
}

export function SelectionCriteriaFields({
  defaultValue,
  defaultMethod,
  hideTitle,
}: Props) {
  const { t } = useTranslations(translations);
  const { trackDontIncludeSelectionCriteria } = useTracking();

  const [isLoading, setIsLoading] = useState(false);

  const { watch, setValue, trigger, setError, clearErrors, formState } =
    useFormContext<SelectionCriteriaFieldsValues>();

  const { validateMethod, validateFile, validateText } = useValidators({
    currentMethod: watch('selectionCriteria-method') || defaultMethod,
  });

  useVirusScan({
    fileUri: watch('selectionCriteria-uri'),
    currentMethod: watch('selectionCriteria-method'),
    onStatusChange: (virusScanStatus, method) => {
      const shouldCheck = method === FileAttachment.UPLOAD;

      if (!shouldCheck) {
        clearErrors('selectionCriteria-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        setError('selectionCriteria-virusScan', {
          message: t('Virus Detected'),
        });
      } else {
        clearErrors('selectionCriteria-virusScan');
      }

      setIsLoading(virusScanStatus === VirusScanStatus.Pending);
    },
  });

  const handleSelectionCriteriaChange = async (file?: File) => {
    if (!file) return;

    try {
      setIsLoading(true);
      const uri = await uploadSelectionCriteria({ selectionCriteria: file });
      flushSync(() => setValue('selectionCriteria-uri', uri));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // handle error
    } finally {
      trigger('selectionCriteria-file'); // trigger validation
    }
  };

  return (
    <AttachmentSelect
      hideTitle={hideTitle}
      title={t('Selection criteria')}
      name="selectionCriteria-method"
      labelNone={t('Already addressed in resumé or cover letter')}
      defaultValue={defaultMethod}
      onChange={(method) => {
        if (method === FileAttachment.NONE) {
          trackDontIncludeSelectionCriteria();
        }
      }}
      validate={validateMethod}
    >
      <AttachmentOptionFile
        testid="selectionCriteriaFileInput"
        name="selectionCriteria-file"
        label={t('Upload a statement')}
        description={t('Accepted file types')}
        reUploadText={t('Upload a different statement')}
        validate={validateFile}
        onChange={handleSelectionCriteriaChange}
        defaultValue={isFile(defaultValue) ? defaultValue : undefined}
        isLoading={isLoading}
        message={formState.errors['selectionCriteria-virusScan']?.message}
      />
      <AttachmentOptionText
        testid="selectionCriteriaTextInput"
        name="selectionCriteria-text"
        label={t('Write a statement')}
        limit={MAX_CHARS}
        validate={validateText}
        defaultValue={
          typeof defaultValue === 'string' ? defaultValue : undefined
        }
      />
    </AttachmentSelect>
  );
}

export function useTracking() {
  const currentPage = useGetCurrentPage();
  const props = { currentPage };
  const client = useTrackingClient();

  function trackDontIncludeSelectionCriteria() {
    client.trackLink({
      ...props,
      link: 'selection_criteria_not_included_pressed',
      eventName: 'selection_criteria_not_included_pressed',
      currentPageSection: PageSection.SELECTION_CRITERIA,
    });
  }

  return { trackDontIncludeSelectionCriteria };
}
